import React, { useState } from "react";
import { useParams, Link } from "react-router-dom";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Loader2, ArrowLeft, ShoppingCart, ShieldCheck, MapPin } from "lucide-react";
import { toast } from "sonner";
import {
    Accordion,
    AccordionContent,
    AccordionItem,
    AccordionTrigger,
} from "@/components/ui/accordion";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function GuestOrderTracking() {
    const { orderId } = useParams();

    const [name, setName] = useState("");
    const [phone, setPhone] = useState("");
    const [loading, setLoading] = useState(false);
    const [isVerified, setIsVerified] = useState(false);
    const [order, setOrder] = useState(null);

    const handleVerify = async (e) => {
        e.preventDefault();
        if (!name.trim() || !phone.trim()) {
            toast.error("Please enter both Name and Phone number.");
            return;
        }

        setLoading(true);
        try {
            const response = await axios.post(`${API}/guest/verify`, {
                order_id: orderId,
                name,
                phone
            });

            if (response.data.success && response.data.order) {
                setOrder(response.data.order);
                setIsVerified(true);
                toast.success("Identity verified successfully.");
            }
        } catch (err) {
            console.error(err);
            toast.error(err.response?.data?.error || "Verification failed. Details do not match.");
        } finally {
            setLoading(false);
        }
    };

    const formatDate = (dateString) => {
        if (!dateString) return "N/A";
        return new Date(dateString).toLocaleDateString('en-US', {
            year: 'numeric', month: 'long', day: 'numeric',
            hour: '2-digit', minute: '2-digit'
        });
    };

    if (isVerified && order) {
        return (
            <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4 py-12">
                <div className="card-elevated p-8 max-w-2xl w-full">
                    <div className="flex items-center justify-between mb-6">
                        <h2 className="text-2xl font-bold text-slate-900">Order Details</h2>
                        <span className={`px-3 py-1 rounded-full text-xs font-semibold capitalize ${order.status === 'delivered' ? 'bg-lime-100 text-lime-700' :
                            order.status === 'shipped' ? 'bg-blue-100 text-blue-700' :
                                order.status === 'cancelled' ? 'bg-red-100 text-red-700' :
                                    'bg-amber-100 text-amber-700'
                            }`}>
                            {order.status || 'Processing'}
                        </span>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
                        <div className="bg-white p-4 rounded-xl border border-slate-200 shadow-sm">
                            <h3 className="text-sm font-bold text-slate-500 uppercase tracking-wider mb-2">Order Info</h3>
                            <p className="text-slate-900 font-medium">#{order.order_number}</p>
                            <p className="text-slate-500 text-sm mt-1">{formatDate(order.created_at)}</p>
                        </div>
                        <div className="bg-white p-4 rounded-xl border border-slate-200 shadow-sm">
                            <h3 className="text-sm font-bold text-slate-500 uppercase tracking-wider mb-2 flex items-center"><MapPin className="w-4 h-4 mr-1" /> Shipping</h3>
                            <p className="text-slate-900 font-medium">{order.shipping_address?.name || order.customer_name}</p>
                            <p className="text-slate-500 text-sm mt-1">{order.shipping_address?.line1}</p>
                            {order.shipping_address?.line2 && <p className="text-slate-500 text-sm">{order.shipping_address.line2}</p>}
                            <p className="text-slate-500 text-sm">{order.shipping_address?.city}, {order.shipping_address?.state} {order.shipping_address?.postal_code}</p>
                        </div>
                    </div>

                    <div className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden mb-6">
                        <Accordion type="single" collapsible defaultValue="order-items" className="w-full">
                            <AccordionItem value="order-items" className="border-b-0">
                                <AccordionTrigger className="px-6 py-4 hover:bg-slate-50 hover:no-underline transition-colors">
                                    <div className="flex items-center space-x-2">
                                        <ShoppingCart className="w-5 h-5 text-indigo-600" />
                                        <h3 className="font-semibold text-slate-900 text-base">Items Summary</h3>
                                    </div>
                                </AccordionTrigger>
                                <AccordionContent className="px-6 pb-4 pt-2">
                                    <div className="space-y-4">
                                        {order.items?.map((item, index) => (
                                            <div key={index} className="flex gap-4 items-center">
                                                <div className="w-16 h-16 rounded-md bg-slate-50 border border-slate-100 overflow-hidden flex-shrink-0">
                                                    {item.image_url ? (
                                                        <img src={item.image_url} alt={item.name} className="w-full h-full object-cover" />
                                                    ) : (
                                                        <div className="w-full h-full flex items-center justify-center text-slate-300">
                                                            <ShoppingCart size={20} />
                                                        </div>
                                                    )}
                                                </div>
                                                <div className="flex-1 min-w-0">
                                                    <p className="text-sm font-medium text-slate-900 line-clamp-2">{item.name}</p>
                                                    <p className="text-sm text-slate-500 mt-1">Qty: {item.quantity}</p>

                                                    {/* Customizations display */}
                                                    {item.customizations && (Array.isArray(item.customizations) ? item.customizations.length > 0 : Object.keys(item.customizations).length > 0) && (
                                                        <div className="mt-2 space-y-1 pl-2 border-l border-slate-100 py-0.5">
                                                            {Array.isArray(item.customizations) ? (
                                                                item.customizations.map((cust) => (
                                                                    <div key={cust.id} className="text-[10px]">
                                                                        <div className="flex justify-between font-bold text-slate-600">
                                                                            <span>{cust.name}</span>
                                                                            <span className="text-indigo-600 ml-2">+${cust.total.toFixed(2)}</span>
                                                                        </div>
                                                                        {cust.fields.map((f, i) => (
                                                                            <p key={i} className="text-slate-400 italic leading-tight text-[10px]">
                                                                                {f.label}: {f.value}
                                                                            </p>
                                                                        ))}
                                                                    </div>
                                                                ))
                                                            ) : (
                                                                Object.entries(item.customizations).map(([label, val]) => (
                                                                    <p key={label} className="text-[10px] text-slate-400 italic">
                                                                        <span className="font-medium mr-1 text-slate-500">{label}:</span>
                                                                        {val && typeof val === 'string' && val.includes('T') && !isNaN(Date.parse(val))
                                                                            ? new Date(val).toLocaleDateString()
                                                                            : val === true ? 'Yes' : val}
                                                                    </p>
                                                                ))
                                                            )}
                                                        </div>
                                                    )}
                                                </div>
                                                <div className="text-right flex-shrink-0">
                                                    <span className="font-medium text-slate-900">
                                                        ${((item.display_price || item.base_price || 0) * item.quantity).toFixed(2)}
                                                    </span>
                                                </div>
                                            </div>
                                        ))}
                                    </div>

                                    <div className="border-t border-slate-100 mt-6 pt-4 text-sm space-y-2">
                                        <div className="flex justify-between text-slate-600">
                                            <span>Subtotal</span>
                                            <span className="font-medium text-slate-900">${(order.subtotal || 0).toFixed(2)}</span>
                                        </div>
                                        <div className="flex justify-between text-slate-600">
                                            <span>Shipping</span>
                                            <span className="font-medium text-slate-900">${(order.shipping_cost || 0).toFixed(2)}</span>
                                        </div>
                                        <div className="flex justify-between text-slate-600">
                                            <span>Tax</span>
                                            <span className="font-medium text-slate-900">${(order.tax || 0).toFixed(2)}</span>
                                        </div>
                                    </div>
                                    <div className="border-t border-slate-200 flex justify-between items-center bg-slate-50 -mx-6 px-6 py-4 mt-4">
                                        <span className="font-bold text-slate-900 text-base">Total</span>
                                        <span className="font-bold text-indigo-600 text-xl">${(order.total || 0).toFixed(2)}</span>
                                    </div>
                                </AccordionContent>
                            </AccordionItem>
                        </Accordion>
                    </div>

                    <div className="text-center mt-8">
                        <Link to="/">
                            <Button variant="outline" className="rounded-full">
                                <ArrowLeft className="w-4 h-4 mr-2" />
                                Return to Shop
                            </Button>
                        </Link>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4 animate-fade-in">
            <div className="card-elevated p-8 max-w-md w-full">
                <div className="text-center mb-6">
                    <div className="w-16 h-16 bg-indigo-100 rounded-full flex items-center justify-center mx-auto mb-4">
                        <ShieldCheck className="w-8 h-8 text-indigo-600" />
                    </div>
                    <h2 className="text-2xl font-bold text-slate-900">Secure Order Tracking</h2>
                    <p className="text-slate-600 mt-2 text-sm">
                        Please verify your identity using the details provided during checkout to view order <strong>#{orderId?.split('_')?.pop() || orderId}</strong>.
                    </p>
                </div>

                <form onSubmit={handleVerify} className="space-y-4">
                    <div className="space-y-2">
                        <Label htmlFor="name">Full Name (from Checkout)</Label>
                        <Input
                            id="name"
                            type="text"
                            placeholder="e.g. John Doe"
                            value={name}
                            onChange={(e) => setName(e.target.value)}
                            required
                            className="bg-white border-slate-200"
                        />
                    </div>

                    <div className="space-y-2 mb-6">
                        <Label htmlFor="phone">Phone Number</Label>
                        <Input
                            id="phone"
                            type="tel"
                            placeholder="e.g. 555-0198"
                            value={phone}
                            onChange={(e) => setPhone(e.target.value)}
                            required
                            className="bg-white border-slate-200"
                        />
                    </div>

                    <Button
                        type="submit"
                        className="w-full bg-indigo-600 hover:bg-indigo-700 text-white rounded-xl py-6"
                        disabled={loading}
                    >
                        {loading ? (
                            <>
                                <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                                Verifying...
                            </>
                        ) : (
                            "View Order Details"
                        )}
                    </Button>
                </form>

                <div className="mt-6 text-center">
                    <Link to="/">
                        <Button variant="ghost" className="text-slate-500 hover:text-slate-800">
                            <ArrowLeft className="w-4 h-4 mr-2" />
                            Back to Store
                        </Button>
                    </Link>
                </div>
            </div>
        </div>
    );
}
